import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Plus, Search, Eye, Pencil, Trash2, Package, RefreshCw, FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ComboBOMaster = () => {
  const navigate = useNavigate();
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Selected combo
  const [selectedCombo, setSelectedCombo] = useState(null);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Form state for create/edit
  const [formData, setFormData] = useState({
    combo_barcode: '',
    combo_name: '',
    design_name: '',
    size: '',
    shade: '',
    description: '',
    is_active: true,
    components: [{ component_barcode: '', component_name: '', category: '', quantity: 1 }]
  });

  const fetchCombos = useCallback(async () => {
    try {
      setLoading(true);
      const params = { skip: page * 50, limit: 50 };
      if (search) params.search = search;
      
      const response = await apiClient.get('/combo/bom/list', { params });
      setCombos(response.data.combos || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching combos:', error);
      toast.error('Failed to fetch combo list');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await apiClient.post('/combo/bom/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Uploaded: ${response.data.inserted} new, ${response.data.updated} updated`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      
      setUploadDialogOpen(false);
      setUploadFile(null);
      fetchCombos();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.combo_barcode || !formData.combo_name) {
      toast.error('Combo barcode and name are required');
      return;
    }
    
    if (formData.components.length === 0 || !formData.components[0].component_barcode) {
      toast.error('At least one component is required');
      return;
    }
    
    try {
      await apiClient.post('/combo/bom/create', formData);
      toast.success('Combo BOM created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchCombos();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create combo');
    }
  };

  const handleUpdate = async () => {
    try {
      await apiClient.put(`/combo/bom/${selectedCombo.combo_barcode}`, {
        combo_name: formData.combo_name,
        design_name: formData.design_name,
        size: formData.size,
        shade: formData.shade,
        description: formData.description,
        is_active: formData.is_active,
        components: formData.components
      });
      toast.success('Combo BOM updated successfully');
      setEditDialogOpen(false);
      fetchCombos();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update combo');
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/combo/bom/${selectedCombo.combo_barcode}`);
      toast.success('Combo BOM deactivated');
      setDeleteDialogOpen(false);
      fetchCombos();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete combo');
    }
  };

  const resetForm = () => {
    setFormData({
      combo_barcode: '',
      combo_name: '',
      design_name: '',
      size: '',
      shade: '',
      description: '',
      components: [{ component_barcode: '', component_name: '', category: '', quantity: 1 }]
    });
  };

  const openViewDialog = (combo) => {
    setSelectedCombo(combo);
    setViewDialogOpen(true);
  };

  const openEditDialog = (combo) => {
    setSelectedCombo(combo);
    setFormData({
      combo_barcode: combo.combo_barcode,
      combo_name: combo.combo_name,
      design_name: combo.design_name || '',
      size: combo.size || '',
      shade: combo.shade || '',
      description: combo.description || '',
      is_active: combo.is_active !== false,
      components: combo.components || [{ component_barcode: '', component_name: '', category: '', quantity: 1 }]
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (combo) => {
    setSelectedCombo(combo);
    setDeleteDialogOpen(true);
  };

  const addComponent = () => {
    setFormData(prev => ({
      ...prev,
      components: [...prev.components, { component_barcode: '', component_name: '', category: '', quantity: 1 }]
    }));
  };

  const removeComponent = (index) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  const updateComponent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.map((comp, i) => 
        i === index ? { ...comp, [field]: value } : comp
      )
    }));
  };

  const downloadTemplate = () => {
    const csvContent = `Combo Barcode,Combo Name,Design Name,Size,Shade,Description,Component Name,Category,Components Barcode
COMBO001,Shirt-Dhoti Set A,Design A,L,White,White cotton set,Cotton Shirt,Shirt,SHIRT001
COMBO001,Shirt-Dhoti Set A,Design A,L,White,White cotton set,Cotton Dhoti,Dhoti,DHOTI001
COMBO002,Formal Set B,Design B,M,Blue,Blue formal set,Formal Shirt,Shirt,FSHIRT002
COMBO002,Formal Set B,Design B,M,Blue,Blue formal set,Formal Pants,Pants,PANTS002`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'combo_bom_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="combo-bom-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Combo BOM Master</h1>
                <p className="text-sm text-[#E8DCC8]">Bill of Materials Management</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="combo-bom-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Excel
            </Button>
            <Button
              onClick={() => { resetForm(); setCreateDialogOpen(true); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="combo-bom-create-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Combo
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search combos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-64"
                data-testid="combo-bom-search"
              />
            </div>
            <Button variant="outline" onClick={fetchCombos} data-testid="combo-bom-refresh">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          <div className="text-sm text-gray-600">
            Total: <span className="font-semibold">{total}</span> combos
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Combo Barcode</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Design</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Shade</TableHead>
                <TableHead>Components</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : combos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No combos found. Upload an Excel file or create one manually.
                  </TableCell>
                </TableRow>
              ) : (
                combos.map((combo) => (
                  <TableRow key={combo.combo_barcode}>
                    <TableCell className="font-mono text-sm">{combo.combo_barcode}</TableCell>
                    <TableCell className="font-medium">{combo.combo_name}</TableCell>
                    <TableCell>{combo.design_name || '-'}</TableCell>
                    <TableCell>{combo.size || '-'}</TableCell>
                    <TableCell>{combo.shade || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{combo.components?.length || 0} items</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={combo.is_active ? 'default' : 'destructive'}>
                        {combo.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openViewDialog(combo)}
                          data-testid={`combo-view-${combo.combo_barcode}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(combo)}
                          data-testid={`combo-edit-${combo.combo_barcode}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDeleteDialog(combo)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`combo-delete-${combo.combo_barcode}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {total > 50 && (
            <div className="flex justify-between items-center px-4 py-3 border-t">
              <Button
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {Math.ceil(total / 50)}
              </span>
              <Button
                variant="outline"
                disabled={(page + 1) * 50 >= total}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Upload Combo BOM Excel
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file with combo product definitions and components.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="hidden"
                id="file-upload"
                data-testid="combo-bom-file-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-700">
                  {uploadFile ? uploadFile.name : 'Click to select Excel file'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Supports .xlsx and .xls files
                </p>
              </label>
            </div>
            
            <Button variant="outline" className="w-full" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            <div className="text-xs text-gray-500 space-y-1">
              <p className="font-medium">Expected Columns:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Combo Barcode, Combo Name (required)</li>
                <li>Design Name, Size, Shade, Description (optional)</li>
                <li>Component Name, Category, Components Barcode</li>
              </ul>
              <p className="mt-2">Multiple rows with same Combo Barcode = one combo with multiple components.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Combo BOM</DialogTitle>
            <DialogDescription>Define a new combo product with its components.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Combo Barcode *</Label>
                <Input
                  value={formData.combo_barcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, combo_barcode: e.target.value }))}
                  placeholder="Enter barcode"
                  data-testid="combo-create-barcode"
                />
              </div>
              <div>
                <Label>Combo Name *</Label>
                <Input
                  value={formData.combo_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, combo_name: e.target.value }))}
                  placeholder="Enter name"
                  data-testid="combo-create-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Design Name</Label>
                <Input
                  value={formData.design_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, design_name: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Size</Label>
                <Input
                  value={formData.size}
                  onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Shade</Label>
                <Input
                  value={formData.shade}
                  onChange={(e) => setFormData(prev => ({ ...prev, shade: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Components</Label>
                <Button variant="outline" size="sm" onClick={addComponent}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              
              <div className="space-y-2 border rounded-lg p-3">
                {formData.components.map((comp, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-2 items-end">
                    <div>
                      <Label className="text-xs">Barcode *</Label>
                      <Input
                        value={comp.component_barcode}
                        onChange={(e) => updateComponent(idx, 'component_barcode', e.target.value)}
                        placeholder="SKU"
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input
                        value={comp.component_name}
                        onChange={(e) => updateComponent(idx, 'component_name', e.target.value)}
                        placeholder="Name"
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Category</Label>
                      <Input
                        value={comp.category}
                        onChange={(e) => updateComponent(idx, 'category', e.target.value)}
                        placeholder="Shirt, Dhoti..."
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={comp.quantity}
                        onChange={(e) => updateComponent(idx, 'quantity', parseInt(e.target.value) || 1)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      {formData.components.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeComponent(idx)}
                          className="h-9 text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="combo-create-submit">Create Combo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Combo Details</DialogTitle>
          </DialogHeader>
          
          {selectedCombo && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Combo Barcode</Label>
                  <p className="font-mono font-medium">{selectedCombo.combo_barcode}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Combo Name</Label>
                  <p className="font-medium">{selectedCombo.combo_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Design Name</Label>
                  <p>{selectedCombo.design_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Size</Label>
                  <p>{selectedCombo.size || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Shade</Label>
                  <p>{selectedCombo.shade || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <Badge variant={selectedCombo.is_active ? 'default' : 'destructive'}>
                    {selectedCombo.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              
              {selectedCombo.description && (
                <div>
                  <Label className="text-gray-500">Description</Label>
                  <p>{selectedCombo.description}</p>
                </div>
              )}
              
              <div>
                <Label className="text-gray-500">Components ({selectedCombo.components?.length || 0})</Label>
                <div className="mt-2 border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Barcode</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedCombo.components?.map((comp, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{comp.component_barcode}</TableCell>
                          <TableCell>{comp.component_name || '-'}</TableCell>
                          <TableCell>{comp.category || '-'}</TableCell>
                          <TableCell className="text-right">{comp.quantity || 1}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 pt-2 border-t">
                <p>Created: {selectedCombo.created_at ? new Date(selectedCombo.created_at).toLocaleString() : '-'}</p>
                <p>Updated: {selectedCombo.updated_at ? new Date(selectedCombo.updated_at).toLocaleString() : '-'}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Combo BOM</DialogTitle>
            <DialogDescription>Update combo details and components.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Combo Barcode</Label>
                <Input value={formData.combo_barcode} disabled className="bg-gray-100" />
              </div>
              <div>
                <Label>Combo Name *</Label>
                <Input
                  value={formData.combo_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, combo_name: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <p className="text-xs text-muted-foreground">
                  {formData.is_active ? 'Combo is active and available for assembly' : 'Combo is inactive and hidden from assembly'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium ${formData.is_active ? 'text-green-600' : 'text-red-500'}`}>
                  {formData.is_active ? 'Active' : 'Inactive'}
                </span>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  data-testid="combo-active-toggle"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Design Name</Label>
                <Input
                  value={formData.design_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, design_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Size</Label>
                <Input
                  value={formData.size}
                  onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                />
              </div>
              <div>
                <Label>Shade</Label>
                <Input
                  value={formData.shade}
                  onChange={(e) => setFormData(prev => ({ ...prev, shade: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Components</Label>
                <Button variant="outline" size="sm" onClick={addComponent}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              
              <div className="space-y-2 border rounded-lg p-3">
                {formData.components.map((comp, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-2 items-end">
                    <div>
                      <Label className="text-xs">Barcode *</Label>
                      <Input
                        value={comp.component_barcode}
                        onChange={(e) => updateComponent(idx, 'component_barcode', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input
                        value={comp.component_name}
                        onChange={(e) => updateComponent(idx, 'component_name', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Category</Label>
                      <Input
                        value={comp.category}
                        onChange={(e) => updateComponent(idx, 'category', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={comp.quantity}
                        onChange={(e) => updateComponent(idx, 'quantity', parseInt(e.target.value) || 1)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      {formData.components.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeComponent(idx)}
                          className="h-9 text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} data-testid="combo-edit-submit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deactivate Combo</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate this combo? It will no longer be available for new assembly sessions.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCombo && (
            <div className="py-4">
              <p className="text-sm"><strong>Barcode:</strong> {selectedCombo.combo_barcode}</p>
              <p className="text-sm"><strong>Name:</strong> {selectedCombo.combo_name}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="combo-delete-confirm">
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComboBOMaster;
